/**
 * web/src/hooks/mutations/useResubmitGate.ts
 * Marks a rejected gate as waiting_approval again (engine-side state move).
 * Used to enable the Edit & Resubmit flow on the Approval Queue.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { resubmitGate } from '../../api/gates';
import { toastApiError } from '../../lib/api';

/**
 * Resubmits a rejected gate so the reviewer can edit and approve again.
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST
 * @idempotent yes — second call against an already-waiting gate is a no-op server-side
 */
export function useResubmitGate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { workflowId: string; stepIndex: number }) =>
      resubmitGate(vars.workflowId, vars.stepIndex),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: qk.gates.pending() });
      qc.invalidateQueries({ queryKey: qk.workflow.root(vars.workflowId) });
    },
    onError: (err) => {
      toastApiError(err, 'Resubmit failed');
    },
  });
}
